import { supabase } from '@/lib/supabase/client';

export interface UploadResult {
  url: string;
  path: string;
}

const STORAGE_OBJECT_PATTERN = /\/object\/(?:sign|public|authenticated)\/([^/]+)\/(.+)$/;

/**
 * Upload a file to Supabase Storage
 * @param bucket - Storage bucket name
 * @param path - File path within bucket (e.g., 'locations/{locationId}/logo.jpg')
 * @param file - File to upload
 * @returns Public URL or signed URL for the uploaded file
 */
export async function uploadFile(bucket: string, path: string, file: File): Promise<UploadResult> {
  try {
    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage.from(bucket).upload(path, file, {
      cacheControl: '3600',
      upsert: true, // Overwrite if file exists
    });

    if (error) {
      console.error(`Storage upload error for bucket "${bucket}":`, error);
      throw new Error(`Failed to upload to storage: ${error.message}`);
    }

    if (!data) {
      throw new Error('Upload succeeded but no data returned');
    }

    // Get public URL (for public buckets) or signed URL (for private buckets)
    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
    
    // For private buckets, we'll use signed URLs
    // For now, return the public URL structure (we can switch to signed URLs later if needed)
    return {
      url: urlData.publicUrl,
      path: data.path,
    };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
}

/**
 * Get a signed URL for a private file
 * @param bucket - Storage bucket name
 * @param path - File path within bucket
 * @param expiresIn - URL expiration time in seconds (default: 3600 = 1 hour)
 * @returns Signed URL
 */
export async function getSignedUrl(bucket: string, path: string, expiresIn: number = 3600): Promise<string> {
  try {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn);
    
    if (error) {
      throw error;
    }

    return data.signedUrl;
  } catch (error) {
    console.error('Error getting signed URL:', error);
    throw error;
  }
}

/**
 * Delete a file from Supabase Storage
 * @param bucket - Storage bucket name
 * @param path - File path within bucket
 */
export async function deleteFile(bucket: string, path: string): Promise<void> {
  try {
    const { error } = await supabase.storage.from(bucket).remove([path]);
    
    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
}

/**
 * Upload a location logo
 * @param locationId - Location ID
 * @param file - Logo file
 * @returns Upload result with URL
 */
export async function uploadLocationLogo(locationId: string, file: File): Promise<UploadResult> {
  const fileExt = file.name.split('.').pop();
  const fileName = `logo.${fileExt}`;
  const filePath = `locations/${locationId}/${fileName}`;
  
  return uploadFile('location-logos', filePath, file);
}

/**
 * Upload a location photo
 * @param locationId - Location ID
 * @param file - Photo file
 * @returns Upload result with URL
 */
export async function uploadLocationPhoto(locationId: string, file: File): Promise<UploadResult> {
  const timestamp = Date.now();
  const fileExt = file.name.split('.').pop();
  const fileName = `${timestamp}.${fileExt}`;
  const filePath = `locations/${locationId}/${fileName}`;
  
  return uploadFile('location-photos', filePath, file);
}

/**
 * Delete a location photo
 * @param locationId - Location ID
 * @param fileName - Photo file name
 */
export async function deleteLocationPhoto(locationId: string, fileName: string): Promise<void> {
  const filePath = `locations/${locationId}/${fileName}`;
  return deleteFile('location-photos', filePath);
}

/**
 * Upload a location contract document
 * @param locationId - Location ID
 * @param file - Contract file (PDF or image)
 * @returns Upload result with URL
 */
export async function uploadLocationContract(locationId: string, file: File): Promise<UploadResult> {
  const timestamp = Date.now();
  const fileExt = file.name.split('.').pop();
  const fileName = `contract-${timestamp}.${fileExt}`;
  const filePath = `locations/${locationId}/${fileName}`;
  
  return uploadFile('contracts', filePath, file);
}

function safeStorageFileName(fileName: string): string {
  const normalized = fileName.trim().replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-');
  return normalized || 'document';
}

/**
 * Upload a document attached to a deal before it converts to a location.
 */
export async function uploadDealDocument(dealId: string, file: File): Promise<UploadResult> {
  const timestamp = Date.now();
  const filePath = `deals/${dealId}/documents/${timestamp}-${safeStorageFileName(file.name)}`;
  return uploadFile('contracts', filePath, file);
}

/**
 * Delete a location contract
 * @param locationId - Location ID
 * @param fileName - Contract file name
 */
export async function deleteLocationContract(locationId: string, fileName: string): Promise<void> {
  const filePath = `locations/${locationId}/${fileName}`;
  return deleteFile('contracts', filePath);
}

/**
 * Upload a generated SLA agreement PDF.
 */
export async function uploadSlaAgreementGeneratedPdf(agreementId: string, file: File): Promise<UploadResult> {
  const timestamp = Date.now();
  const filePath = `sla-agreements/${agreementId}/generated/${timestamp}-${file.name}`;
  return uploadFile('contracts', filePath, file);
}

/**
 * Upload a signed SLA agreement artifact.
 */
export async function uploadSlaAgreementSignedFile(agreementId: string, file: File): Promise<UploadResult> {
  const timestamp = Date.now();
  const filePath = `sla-agreements/${agreementId}/signed/${timestamp}-${file.name}`;
  return uploadFile('contracts', filePath, file);
}

/**
 * Upload a finalized SLA agreement artifact.
 */
export async function uploadSlaAgreementFinalizedFile(agreementId: string, file: File): Promise<UploadResult> {
  const timestamp = Date.now();
  const filePath = `sla-agreements/${agreementId}/finalized/${timestamp}-${file.name}`;
  return uploadFile('contracts', filePath, file);
}

function normalizePathLikeValue(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (!trimmed.startsWith('http')) {
    return trimmed.split('?')[0] || null;
  }

  try {
    const parsedUrl = new URL(trimmed);
    const decodedPathname = decodeURIComponent(parsedUrl.pathname);
    const storageMatch = decodedPathname.match(STORAGE_OBJECT_PATTERN);
    if (storageMatch?.[2]) {
      return storageMatch[2];
    }
  } catch (error) {
    console.error('Error parsing storage URL:', error);
  }

  return null;
}

export function getContractStoragePath(
  contractFilePath: string | null | undefined,
  contractFileUrl: string | null | undefined
): string | null {
  return normalizePathLikeValue(contractFilePath) || normalizePathLikeValue(contractFileUrl);
}

export async function getContractFileUrl(
  contractFilePath: string | null | undefined,
  contractFileUrl: string | null | undefined,
  expiresIn: number = 3600
): Promise<string | null> {
  const storagePath = getContractStoragePath(contractFilePath, contractFileUrl);
  if (storagePath) {
    try {
      return await getSignedUrl('contracts', storagePath, expiresIn);
    } catch (error) {
      console.error('Error generating signed URL for contract:', error);
      return null;
    }
  }

  if (contractFileUrl?.trim()) {
    return contractFileUrl.trim();
  }

  return null;
}

export function getContractFileName(
  contractFilePath: string | null | undefined,
  contractFileUrl: string | null | undefined
): string | null {
  const storagePath = getContractStoragePath(contractFilePath, contractFileUrl);
  if (storagePath) {
    return storagePath.split('/').pop() || storagePath;
  }

  if (!contractFileUrl) {
    return null;
  }

  try {
    const parsedUrl = new URL(contractFileUrl);
    return decodeURIComponent(parsedUrl.pathname.split('/').pop() || '') || null;
  } catch {
    return contractFileUrl.split('/').pop()?.split('?')[0] || null;
  }
}

export function hasContractFileReference(input: {
  contract_file_path?: string | null;
  contract_file_url?: string | null;
} | null | undefined): boolean {
  if (!input) {
    return false;
  }

  return Boolean(getContractStoragePath(input.contract_file_path, input.contract_file_url) || input.contract_file_url?.trim());
}

/**
 * Get a signed URL for a location logo
 * Handles both path-based storage and legacy signed URL storage
 * @param logoUrlOrPath - Either a signed URL (legacy) or a file path
 * @param locationId - Location ID (used if logoUrlOrPath is a signed URL or needs path construction)
 * @param expiresIn - URL expiration time in seconds (default: 1 year)
 * @returns Signed URL
 */
export async function getLocationLogoUrl(
  logoUrlOrPath: string | null,
  locationId: string,
  expiresIn: number = 31536000 // 1 year in seconds
): Promise<string | null> {
  if (!logoUrlOrPath) return null;

  let filePath: string;

  // Check if it's already a signed URL (contains token parameter or is a full URL)
  if (logoUrlOrPath.includes('token=') || logoUrlOrPath.startsWith('http')) {
    // It's a signed URL or full URL - extract the path
    try {
      // Extract path from URL
      // Format: https://.../storage/v1/object/sign/location-logos/locations/{locationId}/logo.jpg?token=...
      // Or: locations/{locationId}/logo.jpg?token=...
      const urlParts = logoUrlOrPath.split('/');
      let fileName = urlParts[urlParts.length - 1] || '';
      
      // Remove query parameters if present
      if (fileName.includes('?')) {
        fileName = fileName.split('?')[0] || '';
      }
      
      // Construct the path
      filePath = `locations/${locationId}/${fileName}`;
    } catch (error) {
      console.error('Error extracting path from logo URL:', error);
      // Fallback: try to use the stored value as a path
      filePath = logoUrlOrPath;
    }
  } else {
    // It's already a path - use it directly
    filePath = logoUrlOrPath;
  }

  // Generate fresh signed URL with long expiration
  try {
    return await getSignedUrl('location-logos', filePath, expiresIn);
  } catch (error) {
    console.error('Error generating signed URL for logo:', error);
    return null;
  }
}

/**
 * Upload a packing slip PDF
 * @param locationId - Location ID
 * @param file - PDF file
 * @returns Upload result with URL
 */
export async function uploadPackingSlip(locationId: string, file: File): Promise<UploadResult> {
  const timestamp = Date.now();
  const fileName = `packing-slip-${timestamp}.pdf`;
  const filePath = `locations/${locationId}/${fileName}`;
  
  return uploadFile('packing-slips', filePath, file);
}

/**
 * Get a signed URL for a packing slip PDF
 * @param filePath - File path in storage bucket
 * @param expiresIn - URL expiration time in seconds (default: 1 hour)
 * @returns Signed URL
 */
export async function getPackingSlipUrl(filePath: string, expiresIn: number = 3600): Promise<string> {
  return getSignedUrl('packing-slips', filePath, expiresIn);
}

/**
 * Delete a packing slip PDF
 * @param filePath - File path in storage bucket
 */
export async function deletePackingSlip(filePath: string): Promise<void> {
  return deleteFile('packing-slips', filePath);
}
